/**
 * AI 코드 수리/생성 오케스트레이션 — 결정 #30 3단계(web). core의 runRepairLoop(3a)를
 * 웹 AI 게이트웨이(결정 #7)와 활성 프로바이더에 연결한다. 도메인별 검증기·시스템
 * 프롬프트를 여기서 정하고, 루프 자체(반복 캡·무진전 중단·토큰 보호)는 core가 맡는다.
 *
 * 보안(결정 #17): AI가 만든/고친 코드도 저장·실행 시 동일한 화이트리스트·샌드박스를
 * 통과한다. 이 루프는 "검증을 통과할 때까지 고치게" 도울 뿐, 신뢰 경계가 아니다.
 */
import {
  lintGameDefinition,
  parseRoqeetGameSource,
  providerSupportsCapability,
  quickFixFromError,
  runRepairLoop,
  type ChatTurn,
  type MockApiOptions,
  type ProviderProfile,
  type RepairLang,
  type RepairLoopOptions,
  type RepairOutcome,
  type RepairStep,
  type ScriptAssertion,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { aiGateway } from "./ai.js";
import { checkJsSyntax } from "../platform/script/js-syntax.js";
import { runScriptTest } from "./script-test.js";

/** 수리 대상 종류. 검증기·문법·시스템 프롬프트를 결정한다. */
export type RepairKind = "rqg-json" | "javascript" | "lua";

export interface AiRepairInput {
  kind: RepairKind;
  source: string;
  /** 대화형 편집 지시(선택). 없으면 순수 오류 수정. */
  instruction?: string;
  /** 이어가는 대화(후속 턴). */
  priorMessages?: ChatTurn[];
  maxIterations?: number;
  signal?: AbortSignal;
  onStep?: (step: RepairStep) => void;
}

/** 활성 프로바이더 프로필 해석(설정 기본 → 첫 활성). chat-actions와 같은 규칙. */
async function resolveProfile(): Promise<ProviderProfile> {
  const settings = await storage.getSettings();
  const profiles = await storage.providerProfiles.list();
  const enabled = profiles.filter((p) => p.enabled && providerSupportsCapability(p, "chat"));
  const id = settings?.defaultProviderProfileId;
  const chosen = (id && enabled.find((p) => p.id === id)) || enabled[0];
  if (!chosen) {
    throw new Error(
      "사용 가능한 AI 프로바이더가 없습니다. 설정에서 프로바이더와 API 키를 먼저 추가하세요.",
    );
  }
  return chosen;
}

/** 종류 → core 균형 검사기 문법. rqg-json은 JSON이라 괄호 체계가 javascript와 동일. */
function repairLang(kind: RepairKind): RepairLang {
  switch (kind) {
    case "lua":
      return "lua";
    case "javascript":
    case "rqg-json":
    default:
      return "javascript";
  }
}

/** 종류별 검증기: 오류 메시지 배열 반환(빈 배열 = 유효). 절대 throw하지 않는다.
 *  JS는 Acorn(lazy-load) 구문 검사라 비동기다(core 루프가 await로 처리). */
function validatorFor(kind: RepairKind): (source: string) => string[] | Promise<string[]> {
  if (kind === "rqg-json") {
    return (source: string) => {
      try {
        const def = parseRoqeetGameSource(source).definition;
        // 파싱은 통과해도 "조용한 오작동"(린트 error)은 막는다(결정 #30).
        return lintGameDefinition(def)
          .filter((i) => i.severity === "error")
          .map((i) => i.message);
      } catch (e) {
        return [e instanceof Error ? e.message : String(e)];
      }
    };
  }
  if (kind === "javascript") {
    // 진짜 JS 구문 검사(Acorn). ASI로 못 살리는 세미콜론 오류·잘못된 토큰 등을 잡는다.
    return (source: string) => checkJsSyntax(source);
  }
  // Lua: JS 파서로 검사할 수 없어 결정적 균형 검사(사전패스)에 의존한다. 전용 Lua
  // 구문/실행 검증은 후속(테스트 환경) 단계에서 추가한다.
  return () => [];
}

/** 종류별 시스템 프롬프트. AI에게 출력 포맷과 스키마/제약을 가르친다. */
function systemPromptFor(kind: RepairKind): string {
  if (kind === "rqg-json") {
    return [
      "당신은 RoQeet Launcher의 게임 .rqg 문서 작성 도우미입니다.",
      "출력은 항상 유효한 JSON 객체 하나이며, 설명 없이 코드블록(```json)으로만 답합니다.",
      '루트 필수 필드: kind="roqeet.game", rqtVersion="0.1", formatVersion="0.1", nodes(배열).',
      "필수 노드: game.meta(title), game.initialState(values), game.aiContract.",
      "서술 가이드는 game.narrationGuide(text)에 넣고, 게임 규칙은 instructions 텍스트가 아니라 typed nodes로 선언하세요.",
      "규칙/상태 노드: game.mutableKeys(keys), game.clamps(clamps), game.rules(rules), game.dice(rule), game.endings(conditions), game.view(view).",
      "AI는 narration-and-proposed-actions만 내며, 엔진이 validate-and-apply합니다.",
      "op은 lte|gte|lt|gt|eq|neq|truthy|falsy 중 하나입니다.",
      "AI가 제안할 수 있는 상태 키는 game.mutableKeys에 모두 나열해야 합니다(누락 시 무시됨).",
      "임의 코드·함수·알 수 없는 필드를 넣지 마세요. 선언적 데이터만 사용합니다.",
    ].join("\n");
  }
  const lang = kind === "lua" ? "Lua" : "JavaScript";
  return [
    `당신은 RoQeet Launcher의 트리거 스크립트(${lang}) 작성 도우미입니다.`,
    "스크립트는 격리 샌드박스에서 실행되며 호스트가 주입한 API만 쓸 수 있습니다.",
    "사용 가능: context.text(변형할 텍스트), context.hook, setResult(값)으로 결과 반환,",
    "getVar/setVar(변수 권한 시), log, random(), now(). DOM·네트워크·파일 접근은 불가합니다.",
    "출력은 설명 없이 코드블록(```)으로 전체 코드만, 부분/생략 없이 작성하세요.",
    "괄호·end·따옴표 짝을 정확히 맞추세요(작은 모델이 자주 틀리는 부분).",
  ].join("\n");
}

/**
 * AI 수리/생성 루프를 실행한다(결정 #30). 활성 프로바이더·모델을 해석하고 종류별
 * 검증기·프롬프트로 core 루프를 돌린다. 결과(최종 소스 + 단계 + 남은 오류 + 대화)를
 * 반환해 UI가 diff 확인 후 적용하게 한다.
 */
export async function aiRepair(input: AiRepairInput): Promise<RepairOutcome> {
  const profile = await resolveProfile();
  const model = profile.defaultModel;
  if (!model) {
    throw new Error(
      "선택된 프로바이더에 기본 모델이 없습니다. 설정에서 모델을 지정하세요.",
    );
  }

  const options: RepairLoopOptions = {
    source: input.source,
    lang: repairLang(input.kind),
    validate: validatorFor(input.kind),
    gateway: aiGateway,
    profile,
    model,
    systemPrompt: systemPromptFor(input.kind),
    ...(input.instruction ? { instruction: input.instruction } : {}),
    ...(input.priorMessages ? { priorMessages: input.priorMessages } : {}),
    ...(input.maxIterations ? { maxIterations: input.maxIterations } : {}),
    ...(input.signal ? { signal: input.signal } : {}),
    ...(input.onStep ? { onStep: input.onStep } : {}),
  };

  return runRepairLoop(options);
}

/** 테스트 기반 런타임 수리 입력(스크립트 전용). 테스트 패널의 입력/변수/기대값을 쓴다. */
export interface AiTestRepairInput {
  language: "javascript" | "lua";
  source: string;
  /** context.text 초기값(테스트 패널과 동일). */
  inputText: string;
  /** 모킹 상태(변수·시드 등). */
  mock?: MockApiOptions;
  /** 기대값(있으면 어서션 실패도 수리 대상에 포함). */
  assertion?: ScriptAssertion;
  maxIterations?: number;
  signal?: AbortSignal;
  onStep?: (step: RepairStep) => void;
}

/**
 * 테스트 실행 결과를 검증기로 삼아 스크립트를 자동 수리한다(결정 #30). 구문 검사(aiRepair)와
 * 달리, **실제 샌드박스에서 돌려** 런타임 오류·기대값 실패를 잡아 AI에 먹이고 반복한다.
 * "테스트가 통과할 때까지 고치기"다. 토큰을 쓰므로 호출부는 테스트가 실패했을 때만 노출한다.
 *
 * 안전장치는 core 루프가 그대로 제공(반복 캡·무진전 중단). 검증기가 이미 통과면 AI를
 * 호출하지 않는다(토큰 0).
 */
export async function aiRepairWithTest(input: AiTestRepairInput): Promise<RepairOutcome> {
  const profile = await resolveProfile();
  const model = profile.defaultModel;
  if (!model) {
    throw new Error("선택된 프로바이더에 기본 모델이 없습니다. 설정에서 모델을 지정하세요.");
  }

  // 검증기: 실제 샌드박스 실행 결과를 오류 메시지로 변환(빈 배열 = 통과).
  const validate = async (source: string): Promise<string[]> => {
    const res = await runScriptTest({
      language: input.language,
      source,
      inputText: input.inputText,
      ...(input.mock ? { mock: input.mock } : {}),
      ...(input.assertion ? { assertion: input.assertion } : {}),
    });
    const errors: string[] = [];
    if (!res.ok && res.error) errors.push(`실행 오류: ${res.error}`);
    if (res.assertion && !res.assertion.pass) errors.push(...res.assertion.failures);
    return errors;
  };

  const options: RepairLoopOptions = {
    source: input.source,
    lang: repairLang(input.language),
    validate,
    gateway: aiGateway,
    profile,
    model,
    systemPrompt: systemPromptFor(input.language),
    ...(input.maxIterations ? { maxIterations: input.maxIterations } : {}),
    ...(input.signal ? { signal: input.signal } : {}),
    ...(input.onStep ? { onStep: input.onStep } : {}),
  };

  return runRepairLoop(options);
}

/** 빠른 수정(AI 0) 결과. */
export interface QuickRepairOutcome {
  /** 수정 후 테스트가 통과하는지. */
  ok: boolean;
  /** 최종 소스(수정 적용본, 변경 없으면 원본과 동일). */
  source: string;
  /** 적용된 결정적 수정 설명들(사람이 읽는 diff 근거). */
  applied: string[];
  /** 끝까지 남은 실행 오류(빠른 수정으로 못 고친 것). */
  remainingError?: string;
  /** 변경이 한 건이라도 있었는지. */
  changed: boolean;
}

/**
 * 에러 주도 빠른 수정(결정 #30, AI 0). 테스트를 실제 샌드박스에서 돌려 실행 오류가
 * 나면, 에러 메시지가 가리키는 실패 심볼을 알려진 API와 매칭해 오타를 결정적으로
 * 교정하고 재실행한다. 통과하거나 더 고칠 게 없을 때까지 반복(상한·무진전 중단).
 *
 * 한계(정직): 로직 오류·사용자 변수 오타는 의도를 알 수 없어 못 고친다 → 그건 AI 몫.
 * 그래서 이 함수가 실패하면 호출부가 AI 수리를 권한다. 토큰 0이므로 항상 먼저 시도해도
 * 안전하다.
 */
export async function quickRepairWithTest(
  input: AiTestRepairInput,
): Promise<QuickRepairOutcome> {
  let source = input.source;
  const applied: string[] = [];
  const seen = new Set<string>();
  const MAX = 6;

  for (let i = 0; i < MAX; i++) {
    if (input.signal?.aborted) break;
    const res = await runScriptTest({
      language: input.language,
      source,
      inputText: input.inputText,
      ...(input.mock ? { mock: input.mock } : {}),
      ...(input.assertion ? { assertion: input.assertion } : {}),
    });

    // 실행 오류가 없으면(어서션 실패는 결정적으로 못 고침) 여기서 끝.
    if (res.ok) {
      const assertionFailed = !!res.assertion && !res.assertion.pass;
      return {
        ok: !assertionFailed,
        source,
        applied,
        changed: applied.length > 0,
        ...(assertionFailed
          ? { remainingError: "기대값 불일치(로직)는 빠른 수정 대상이 아닙니다." }
          : {}),
      };
    }

    // 실행 오류 → 에러 메시지로 빠른 수정 시도.
    const errMsg = res.error ?? "";
    const fix = quickFixFromError(source, errMsg);
    // 더 못 고치거나(후보 없음) 같은 수정을 반복하면 중단(무진전).
    if (!fix || seen.has(fix.description)) {
      return {
        ok: false,
        source,
        applied,
        changed: applied.length > 0,
        remainingError: errMsg,
      };
    }
    seen.add(fix.description);
    source = fix.fixed;
    applied.push(fix.description);
    input.onStep?.({ iteration: i + 1, phase: "ai", source, errors: [], note: fix.description });
  }

  // 상한 도달: 마지막 상태로 한 번 더 평가.
  const final = await runScriptTest({
    language: input.language,
    source,
    inputText: input.inputText,
    ...(input.mock ? { mock: input.mock } : {}),
    ...(input.assertion ? { assertion: input.assertion } : {}),
  });
  return {
    ok: final.ok && (!final.assertion || final.assertion.pass),
    source,
    applied,
    changed: applied.length > 0,
    ...(final.error ? { remainingError: final.error } : {}),
  };
}
